import React, { useState, useEffect, useMemo } from 'react';
import { X, Minus, Plus, TrendingUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { sportStatsService } from '../../services/sportStatsService';
import type { Sport, SportStat, StatWithId } from '../../types';
import { FOOTBALL_STAT_SECTIONS } from '../../data/footballStatSections';

interface FootballGameStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerCardId: string;
  teamId: string;
  sport: Sport;
  onStatsAdded: () => void;
  gameId?: string;
  existingStats?: StatWithId[];
  playerName?: string;
}

// ── Stat section definitions ──────────────────────────────────────────────────
// Section data lives in src/data/footballStatSections.ts so its key list can be
// asserted against SPORT_STAT_KEYS without importing this component.

const SECTIONS = FOOTBALL_STAT_SECTIONS;

// ── Season summary calculations ───────────────────────────────────────────────

interface SeasonTotals {
  [key: string]: number;
}

function aggregateSeasonStats(allStats: SportStat[]): {
  totals: SeasonTotals;
  passingGames: number;
  rushingGames: number;
  receivingGames: number;
} {
  const totals: SeasonTotals = {};
  for (const s of allStats) {
    totals[s.stat_key] = (totals[s.stat_key] ?? 0) + s.stat_value;
  }

  // Count distinct games (by game_id) that have the primary yardage stat
  const passingGames = new Set(
    allStats.filter((s) => s.stat_key === 'PassYds' && s.game_id).map((s) => s.game_id)
  ).size;
  const rushingGames = new Set(
    allStats.filter((s) => s.stat_key === 'RushYds' && s.game_id).map((s) => s.game_id)
  ).size;
  const receivingGames = new Set(
    allStats.filter((s) => s.stat_key === 'RecYds' && s.game_id).map((s) => s.game_id)
  ).size;

  return { totals, passingGames, rushingGames, receivingGames };
}

function calcPasserRating(totals: SeasonTotals): number | null {
  const att = totals['PassAtt'] ?? 0;
  if (att === 0) return null;
  const comp = totals['PassComp'] ?? 0;
  const yds = totals['PassYds'] ?? 0;
  const td = totals['PassTD'] ?? 0;
  const int = totals['PassInt'] ?? 0;
  const a = Math.min(Math.max(((comp / att) * 100 - 30) / 20, 0), 2.375);
  const b = Math.min(Math.max((yds / att - 3) * 0.25, 0), 2.375);
  const c = Math.min(Math.max((td / att) * 20, 0), 2.375);
  const d = Math.min(Math.max(2.375 - (int / att) * 25, 0), 2.375);
  return ((a + b + c + d) / 6) * 100;
}

function fmt(n: number, decimals = 1): string {
  return n.toFixed(decimals);
}

// ── Stepper field ─────────────────────────────────────────────────────────────

interface StepperFieldProps {
  value: string;
  onChange: (v: string) => void;
  step?: number;
  label: string;
}

function StepperField({ value, onChange, step = 1, label }: StepperFieldProps) {
  const adjust = (delta: number) => {
    const cur = parseFloat(value) || 0;
    const next = Math.max(0, cur + delta * step);
    onChange(step === 1 ? String(Math.round(next)) : fmt(next));
  };
  return (
    <div className="flex items-center rounded-md border border-gray-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary">
      <button
        type="button"
        onClick={() => adjust(-1)}
        className="w-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-50 py-1.5 shrink-0"
        aria-label={`Decrease ${label}`}
      >
        <Minus size={10} strokeWidth={2.5} />
      </button>
      <input
        type="number"
        min={0}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        className="w-8 text-center text-sm py-1.5 border-0 bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        aria-label={label}
      />
      <button
        type="button"
        onClick={() => adjust(1)}
        className="w-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-50 py-1.5 shrink-0"
        aria-label={`Increase ${label}`}
      >
        <Plus size={10} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ── Season summary panel ──────────────────────────────────────────────────────

interface SummaryRow {
  label: string;
  value: string | null;
}

function SeasonSummaryPanel({
  rows,
  textColor,
  loading,
}: {
  rows: SummaryRow[];
  textColor: string;
  loading: boolean;
}) {
  const visible = rows.filter((r) => r.value !== null);
  if (!loading && visible.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-gray-200/80">
      <div className={`flex items-center gap-1.5 mb-2 ${textColor}`}>
        <TrendingUp size={13} />
        <span className="text-xs font-semibold uppercase tracking-wide">Season</span>
      </div>
      {loading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : (
        <div className="grid grid-cols-3 gap-x-4 gap-y-1">
          {visible.map(({ label, value }) => (
            <div key={label} className="flex justify-between gap-1 text-xs">
              <span className="text-gray-500 shrink-0">{label}</span>
              <span className="font-semibold tabular-nums text-gray-800 text-right">{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Helper to build initial values from existing stats ────────────────────────

function buildInitialValues(stats: StatWithId[]): Record<string, string> {
  const vals: Record<string, string> = {};
  for (const s of stats) {
    const cur = parseFloat(vals[s.stat_key] ?? '0') + s.stat_value;
    vals[s.stat_key] = String(cur);
  }
  return vals;
}

// ── Main component ────────────────────────────────────────────────────────────

export const FootballGameStatsModal: React.FC<FootballGameStatsModalProps> = ({
  isOpen,
  onClose,
  playerCardId,
  teamId,
  sport,
  onStatsAdded,
  gameId,
  existingStats,
  playerName,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [seasonRaw, setSeasonRaw] = useState<SportStat[]>([]);
  const [loadingSeason, setLoadingSeason] = useState(false);

  const isFlagFootball = sport === 'FlagFootball';
  const isEditing = !!existingStats && existingStats.length > 0;

  // Populate form when opening
  useEffect(() => {
    if (!isOpen) return;
    setValues(existingStats && existingStats.length > 0 ? buildInitialValues(existingStats) : {});
  }, [isOpen, existingStats]);

  // Load all-season stats for summary panels
  useEffect(() => {
    if (!isOpen || !playerCardId || !teamId) return;
    let cancelled = false;
    setLoadingSeason(true);
    sportStatsService.getStats(playerCardId, teamId).then((stats) => {
      if (!cancelled) setSeasonRaw(stats);
    }).catch(() => {
      if (!cancelled) setSeasonRaw([]);
    }).finally(() => {
      if (!cancelled) setLoadingSeason(false);
    });
    return () => { cancelled = true; };
  }, [isOpen, playerCardId, teamId]);

  const { totals, passingGames, rushingGames, receivingGames } = useMemo(
    () => aggregateSeasonStats(seasonRaw),
    [seasonRaw]
  );

  const passingSummary = useMemo((): SummaryRow[] => {
    const att = totals['PassAtt'] ?? 0;
    const comp = totals['PassComp'] ?? 0;
    const yds = totals['PassYds'] ?? 0;
    const rating = calcPasserRating(totals);
    return [
      { label: 'Passer Rtg', value: rating !== null ? fmt(rating, 1) : null },
      { label: 'Comp %', value: att > 0 ? `${fmt((comp / att) * 100, 1)}%` : null },
      { label: 'Yds/Game', value: passingGames > 0 ? fmt(yds / passingGames, 1) : null },
      { label: '20+ Plays', value: (totals['Pass20Plus'] ?? 0) > 0 ? String(totals['Pass20Plus']) : null },
      { label: '30+ Plays', value: (totals['Pass30Plus'] ?? 0) > 0 ? String(totals['Pass30Plus']) : null },
      { label: '40+ Plays', value: (totals['Pass40Plus'] ?? 0) > 0 ? String(totals['Pass40Plus']) : null },
    ];
  }, [totals, passingGames]);

  const rushingSummary = useMemo((): SummaryRow[] => {
    const att = totals['RushAtt'] ?? 0;
    const yds = totals['RushYds'] ?? 0;
    return [
      { label: 'Yds/Game', value: rushingGames > 0 ? fmt(yds / rushingGames, 1) : null },
      { label: 'YPC', value: att > 0 ? fmt(yds / att, 1) : null },
      { label: '20+ Plays', value: (totals['Rush20Plus'] ?? 0) > 0 ? String(totals['Rush20Plus']) : null },
      { label: '30+ Plays', value: (totals['Rush30Plus'] ?? 0) > 0 ? String(totals['Rush30Plus']) : null },
      { label: '40+ Plays', value: (totals['Rush40Plus'] ?? 0) > 0 ? String(totals['Rush40Plus']) : null },
    ];
  }, [totals, rushingGames]);

  const receivingSummary = useMemo((): SummaryRow[] => {
    const rec = totals['Rec'] ?? 0;
    const yds = totals['RecYds'] ?? 0;
    return [
      { label: 'Yds/Game', value: receivingGames > 0 ? fmt(yds / receivingGames, 1) : null },
      { label: 'Yds/Catch', value: rec > 0 ? fmt(yds / rec, 1) : null },
      { label: '20+ Plays', value: (totals['Rec20Plus'] ?? 0) > 0 ? String(totals['Rec20Plus']) : null },
      { label: '30+ Plays', value: (totals['Rec30Plus'] ?? 0) > 0 ? String(totals['Rec30Plus']) : null },
      { label: '40+ Plays', value: (totals['Rec40Plus'] ?? 0) > 0 ? String(totals['Rec40Plus']) : null },
    ];
  }, [totals, receivingGames]);

  const sectionSummary = (sectionId: string) => {
    if (sectionId === 'passing') return { rows: passingSummary, textColor: 'text-blue-700' };
    if (sectionId === 'rushing') return { rows: rushingSummary, textColor: 'text-green-700' };
    if (sectionId === 'receiving') return { rows: receivingSummary, textColor: 'text-amber-700' };
    return null;
  };

  const get = (key: string) => values[key] ?? '';
  const set = (key: string, val: string) => setValues((prev) => ({ ...prev, [key]: val }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!playerCardId || !teamId) return;
    setSaving(true);
    try {
      if (existingStats && existingStats.length > 0) {
        await Promise.all(existingStats.map((s) => sportStatsService.deleteStat(s.id)));
      }

      let date = new Date().toISOString().split('T')[0];
      let opponent: string | undefined;
      let season: string | undefined;
      if (gameId) {
        try {
          const { gameService } = await import('../../services/gameService');
          const game = await gameService.getGame(gameId);
          if (game) {
            date = game.date;
            opponent = game.opponent_name ?? undefined;
            season = game.season ?? undefined;
          }
        } catch {
          // non-fatal
        }
      }

      const saves = Object.entries(values)
        .filter(([, v]) => {
          const n = parseFloat(v);
          return Number.isFinite(n) && n > 0;
        })
        .map(([key, v]) =>
          sportStatsService.addStat(playerCardId, teamId, key, parseFloat(v), date, gameId, opponent, season)
        );

      await Promise.all(saves);
      toast.success(isEditing ? 'Stats updated' : 'Stats saved');
      onStatsAdded();
      onClose();
    } catch (err: any) {
      console.error('[FootballGameStatsModal] save error:', err);
      toast.error(err?.message ?? 'Failed to save stats');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const tileClass =
    'rounded-lg border border-gray-200 bg-white p-2 shadow-sm hover:border-gray-300 focus-within:ring-2 focus-within:ring-primary focus-within:border-primary flex flex-col gap-1';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[9999] p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="football-stats-modal-title"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl max-w-2xl w-full flex flex-col"
        style={{ maxHeight: 'calc(100vh - 60px)', height: 'calc(100vh - 60px)' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3 flex flex-col gap-0.5">
          <div className="flex items-center justify-between">
            <h2 id="football-stats-modal-title" className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Game Stats' : 'Add Game Stats'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </div>
          {playerName && (
            <p className="text-sm font-medium text-primary truncate" title={playerName}>
              {playerName}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          <form id="football-stats-form" onSubmit={handleSave} className="p-4 space-y-3">
            {SECTIONS.map((section) => {
              const sectionStats = section.stats.filter((s) => !(isFlagFootball && s.footballOnly));
              if (sectionStats.length === 0) return null;
              const summary = sectionSummary(section.id);
              return (
                <div
                  key={section.id}
                  className={`rounded-xl border-2 ${section.border} ${section.bg} p-3`}
                >
                  <h3 className={`text-sm font-bold ${section.text} flex items-center gap-2 mb-3`}>
                    <span className={`inline-block w-2 h-2 rounded-full ${section.dot}`} />
                    {section.label}
                  </h3>

                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {sectionStats.map(({ key, label, step = 1 }) => (
                      <div key={key} className={tileClass}>
                        <div className={`text-xs font-semibold ${section.text} truncate`}>{label}</div>
                        <StepperField
                          value={get(key)}
                          onChange={(v) => set(key, v)}
                          step={step}
                          label={label}
                        />
                      </div>
                    ))}
                  </div>

                  {summary && (
                    <SeasonSummaryPanel
                      rows={summary.rows}
                      textColor={summary.textColor}
                      loading={loadingSeason}
                    />
                  )}
                </div>
              );
            })}
          </form>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="football-stats-form"
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 text-sm"
          >
            {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Save Stats'}
          </button>
        </div>
      </div>
    </div>
  );
};
